import { getChainId, switchChain, watchChainId } from "@wagmi/core";
import { wagmiConfig } from "@/provider/PrivyProvider.tsx";
import { Chip, Popover, Button, Typography, Stack } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { type Config } from "wagmi";

import { TEST_NET_ID } from "@/types/enum";
import { useTranslation } from "react-i18next";
import { ICBase } from "@/components/Icon/ICNet/ICBase";
import { ICSepolia } from "@/components/Icon/ICNet/ICSepolia";
import { ICArbitrum } from "@/components/Icon/ICNet/ICArbitrum";
import { useDispatch } from "react-redux";
import { setNetwork } from "@/redux/slices/NetworkSlice";
import { setChainId } from "@/redux/slices/ChainIdSlice";
import { ICBNBNet } from "@/components/Icon/ICNet/ICBNBNet";

// Network configuration object
const SUPPORTED_NETWORKS = [
  {
    name: "Base",
    chainId: TEST_NET_ID.BASE,
    icon: (props: any) => <ICBase {...props} />,
  },
  {
    name: "Arbitrum",
    chainId: TEST_NET_ID.ARBITRUM,
    icon: (props: any) => <ICArbitrum {...props} />,
  },
  {
    name: "BNB Smart Chain",
    chainId: TEST_NET_ID.BSC_MAIN,
    icon: (props: any) => <ICBNBNet {...props} />,
  },
  // {
  //   name: "Polygon",
  //   chainId: TEST_NET_ID.POLYGON,
  //   icon: (props: any) => <ICPolygon {...props} />,
  // },
];

export default function NetworkSituation() {
  const { t } = useTranslation();
  const chainId = getChainId(wagmiConfig as Config);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [alertContent, setAlertContent] = useState({
    severity: "success" as "success" | "warning",
    message: t("common.connected_network"),
  });
  const [pendingChange, setPendingChange] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  // Get current network info
  const currentNetwork = SUPPORTED_NETWORKS.find(
    (network) => network.chainId === chainId
  );

  // Determine if the current network is supported
  const isNetworkSupported = SUPPORTED_NETWORKS.some(
    (network) => network.chainId === chainId
  );

  useEffect(() => {
    // Only show "Network Connected" for 2 seconds after switching networks
    if (pendingChange) {
      setAlertContent({
        severity: "success",
        message: t("common.connected_network"),
      });
      timeoutRef.current = setTimeout(() => {
        setPendingChange(false);
      }, 2000);
    } else if (!pendingChange && !isNetworkSupported) {
      setAlertContent({
        severity: "warning",
        message: t("common.wrong_network"),
      });
    } else if (!pendingChange && isNetworkSupported) {
      setAlertContent({
        severity: "success",
        message: t("common.connected_network"),
      });
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [chainId, pendingChange, isNetworkSupported, t]);  // Listen for chain change events from the wallet
  useEffect(() => {
    // Initialize with current chain
    dispatch(setChainId(chainId));
    
    // Initialize network name in Redux
    if (currentNetwork) {
      dispatch(setNetwork(currentNetwork.name));
    }
    
    // Set up listener for chain changes
    const unwatch = watchChainId(wagmiConfig as Config, {
      onChange(newChainId) {
        dispatch(setChainId(newChainId));
        
        // Also update network name in Redux
        const network = SUPPORTED_NETWORKS.find(net => net.chainId === newChainId);
        if (network) {
          dispatch(setNetwork(network.name));
        }
      },
    });

    // Cleanup listener on unmount
    return () => {
      unwatch();
    };
  }, [dispatch, chainId, currentNetwork]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchNetwork = async (networkChainId: number) => {
    try {
      await switchChain(wagmiConfig as Config, {
        chainId: networkChainId,
      });
      setPendingChange(true);
      dispatch(setChainId(networkChainId));
      
      // Update network name in Redux
      const network = SUPPORTED_NETWORKS.find(net => net.chainId === networkChainId);
      if (network) {
        dispatch(setNetwork(network.name));
      }
      
      handleClose(); // Close the Popover after successful network switch
    } catch (error) {
      console.error("Failed to switch network:", error);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "network-popover" : undefined;

  // Choose icon and color based on current network
  const getNetworkIcon = () => {
    if (isNetworkSupported && currentNetwork) {
      return currentNetwork.icon({
        width: "20",
        height: "20",
        className:
          alertContent.severity === "success"
            ? "text-green-600"
            : "text-amber-500",
      });
    }

    // Default to Sepolia icon for unsupported networks
    return <ICSepolia width="20" height="20" />;
  };

  // Styling based on network state
  const chipStyle = {
    padding: 1,
    margin: {
      xs: 0,
      md: 1,
    },
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: isNetworkSupported
      ? "rgba(34,197,94,0.08)"
      : "rgba(245,158,11,0.15)",
    color: isNetworkSupported ? "#16a34a" : "#f59e07",
    fontWeight: 600,
    transition: "all 0.3s ease",
    cursor: "pointer", // Always clickable to allow switching networks
    "&:hover": {
      bgcolor: "rgba(23, 23, 23, 0.8)",
      color: "#ffffff",
    },
    "& .MuiChip-icon": {
      color: "inherit",
      marginRight: "4px !important",
      transition: "color 0.3s ease",
    },
    "& .MuiChip-label": {
      padding: "0 8px",
      display: {
        xs: "none", // Hide on mobile
        md: "inline-flex", // Show on desktop
      },
    },
  };

  return (
    <div>
      <button
        onClick={handleClick}
        style={{ background: "transparent", border: "none", padding: 0 }}
        aria-describedby={id}
      >
        <Chip
          icon={getNetworkIcon()}
          label={
            isNetworkSupported && currentNetwork
              ? currentNetwork.name
              : t("common.wrong_network")
          }
          sx={chipStyle}
        />
      </button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "18px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          },
        }}
        PaperProps={{
          elevation: 3,
        }}
      >
        <Stack sx={{ minWidth: "220px" }}>
          <Typography
            sx={{
              p: 1.5,
              px: 2,
              color: "text.secondary",
              fontSize: "0.875rem",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            Select Network
          </Typography>

          {SUPPORTED_NETWORKS.map((network) => (
            <Button
              key={network.chainId}
              onClick={() => handleSwitchNetwork(network.chainId)}
              variant="text"
              startIcon={network.icon({ width: "20", height: "20" })}
              sx={{
                padding: 2,
                width: "100%",
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 500,
                color: "var(--text-primary)",
                backgroundColor:
                  chainId === network.chainId
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(23, 23, 23, 0.08)",
                },
              }}
            >
              {network.name}
              {chainId === network.chainId && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    color: "success.main",
                    ml: "auto",
                    fontWeight: "bold",
                  }}
                >
                  Connected
                </Typography>
              )}
            </Button>
          ))}
        </Stack>
      </Popover>
    </div>
  );
}
